import { useState, useEffect } from "react";
import { db, auth } from "../firebase/config";
import { collection, query, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged, IdTokenResult } from "firebase/auth";

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface SuspensionCheckResult {
  isSuspended: boolean;
  isLoading: boolean;
  schoolName: string;
  schoolID: string | null;
  hasValidClaims: boolean;
}

export const useSuspensionCheck = (): SuspensionCheckResult => {
  const [isSuspended, setIsSuspended] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [schoolName, setSchoolName] = useState("");
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [hasValidClaims, setHasValidClaims] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsLoading(false);
        setHasValidClaims(false);
        return;
      }

      try {
        // Get user claims to find schoolID
        const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        // Select affiliation
        let selectedAffiliation: Affiliation | null = null;
        
        // 1. Try to match the claimSchoolID (current active school)
        if (claimSchoolID) {
          selectedAffiliation = affiliations.find(aff => aff.schoolID === claimSchoolID) || 
                                (defaultAffiliation?.schoolID === claimSchoolID ? defaultAffiliation : null) || null;
        }

        // 2. Fallback to default affiliation
        if (!selectedAffiliation && defaultAffiliation?.schoolID) {
          selectedAffiliation = defaultAffiliation;
        }

        // 3. Fallback to first affiliation with a schoolID
        if (!selectedAffiliation && affiliations.length > 0) {
          selectedAffiliation = affiliations.find(aff => aff.schoolID) || affiliations[0];
        }

        if (selectedAffiliation?.schoolID) {
          const currentSchoolID = selectedAffiliation.schoolID;
          const currentRole = selectedAffiliation.role;
          
          setSchoolID(currentSchoolID);
          setHasValidClaims(true);

          // Check suspension status based on role
          if (user.email) {
            if (currentRole === "school_staff") {
              const teachersRef = collection(db, `schools/${currentSchoolID}/teachers`);
              const q = query(teachersRef, where("email", "==", user.email));
              const querySnapshot = await getDocs(q);

              if (!querySnapshot.empty) {
                const teacherDoc = querySnapshot.docs[0];
                const teacherData = teacherDoc.data();
                setIsSuspended(teacherData.suspended === true);
              } else {
                setIsSuspended(false);
              }
            } else if (currentRole === "student") {
               // Optional: Add student suspension check here if needed
               // const studentsRef = collection(db, `schools/${currentSchoolID}/students`);
               // ...
               setIsSuspended(false);
            } else {
               // Guardians and others
               setIsSuspended(false);
            }

            // Fetch school name
            const schoolDetailsRef = collection(db, `schools/${currentSchoolID}/schoolDetails`);
            const schoolDetailsSnapshot = await getDocs(schoolDetailsRef);
            if (!schoolDetailsSnapshot.empty) {
              const schoolData = schoolDetailsSnapshot.docs[0].data();
              setSchoolName(schoolData.name || "this school");
            }
          }
        } else {
          setHasValidClaims(false);
          setSchoolID(null);
        }
      } catch (error) {
        console.error("Error checking suspension status:", error);
        setHasValidClaims(false);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return { isSuspended, isLoading, schoolName, schoolID, hasValidClaims };
};

export default useSuspensionCheck;
